import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, of, switchMap, tap } from 'rxjs';
import { StatsActions } from './stats.actions';
import { StatsPort } from '../../core/services/stats/stats.port';
import { SwalService } from '../../core/services/alerts/swal.service';

@Injectable()
export class StatsEffects {
  private actions$ = inject(Actions);
  private api = inject(StatsPort);
  private swalService = inject(SwalService);

  loadStats$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StatsActions.loadStats),
      switchMap(({ year }) =>
        this.api.getStats(year).pipe(
          map((stats) => StatsActions.loadStatsSuccess({ stats })),
          catchError((err) =>
            of(StatsActions.loadStatsFailure({ error: err?.message ?? 'Errore caricamento statistiche' }))
          )
        )
      )
    )
  );

  loadYears$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StatsActions.loadYears),
      switchMap(() =>
        this.api.getYears().pipe(
          map((response) => StatsActions.loadYearsSuccess({ years: response })),
          catchError((err) =>
            of(StatsActions.loadYearsFailure({ error: err?.message ?? 'Errore caricamento anni' }))
          )
        )
      )
    )
  );

  loadYearsAndStats$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StatsActions.loadYearsSuccess),
      switchMap(({ years }) => {
        if (years.length === 0) return of();
        const currentYear = new Date().getFullYear();
        const year = years.includes(currentYear) ? currentYear : years[years.length - 1];
        return of(StatsActions.loadStats({ year }));
      })
    )
  );

  loadAndamentiMensili$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StatsActions.loadAndamentiMensili),
      switchMap(({ params }) =>
        this.api.getAndamentiMensili(params).pipe(
          map((response) =>
            StatsActions.loadAndamentiMensiliSuccess({
              items: response.content,
              totalElements: response.totalElements,
              totalPages: response.totalPages,
              pageIndex: response.pageIndex,
            })
          ),
          catchError((err) =>
            of(StatsActions.loadAndamentiMensiliFailure({ error: err?.message ?? 'Errore caricamento andamenti' }))
          )
        )
      )
    )
  );

  showFailureToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          StatsActions.loadStatsFailure,
          StatsActions.loadYearsFailure,
          StatsActions.loadAndamentiMensiliFailure
        ),
        tap(({ error }) => this.swalService.apiError(error))
      ),
    { dispatch: false }
  );

  loadPie$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StatsActions.loadPie),
      switchMap(({ anno }) =>
        this.api.getPie(anno).pipe(
          map(data => StatsActions.loadPieSuccess({ data })),
          catchError(error =>
            of(StatsActions.loadPieFailure({ error: error?.message ?? 'Errore caricamento grafico torta' }))
          )
        )
      )
    )
  );

  loadFlussoMensile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StatsActions.loadFlussoMensile),
      switchMap(({ anno }) =>
        this.api.getFlussoMensile(anno).pipe(
          map(data => StatsActions.loadFlussoMensileSuccess({ data })),
          catchError(error =>
            of(StatsActions.loadFlussoMensileFailure({ error: error?.message ?? 'Errore caricamento flusso mensile' }))
          )
        )
      )
    )
  );
}
